import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, guestId } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Play, Mic2, Clock, Flame, Trophy, ArrowRight } from "lucide-react";

function useCountdown() {
  const [txt, setTxt] = useState("");
  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const next = new Date(now);
      next.setHours(24, 0, 0, 0);
      const d = next - now;
      const h = Math.floor(d / 3.6e6);
      const m = Math.floor((d % 3.6e6) / 6e4);
      const s = Math.floor((d % 6e4) / 1000);
      setTxt(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  return txt;
}

export default function Landing() {
  const nav = useNavigate();
  const { user } = useAuth();
  const countdown = useCountdown();
  const [theme, setTheme] = useState({ played: false, won: false });
  const [promo, setPromo] = useState({ played: false, won: false });
  const [themeInfo, setThemeInfo] = useState(null);
  const [promoInfo, setPromoInfo] = useState(null);
  const [stats, setStats] = useState({ streak: 0, score: 0 });

  useEffect(() => {
    (async () => {
      const [t, p] = await Promise.all([
        api.get("/puzzles/daily/theme").catch(() => ({ data: { puzzle: null } })),
        api.get("/puzzles/daily/promo").catch(() => ({ data: { puzzle: null } })),
      ]);
      setThemeInfo(t.data);
      setPromoInfo(p.data);
      if (t.data.puzzle) {
        const r = await api.get(`/play/result/${t.data.puzzle.id}`,
          { params: user ? {} : { guest_id: guestId() } });
        setTheme(r.data);
      }
      if (p.data.puzzle) {
        const r = await api.get(`/play/result/${p.data.puzzle.id}`,
          { params: user ? {} : { guest_id: guestId() } });
        setPromo(r.data);
      }
      if (user) {
        const s = await api.get("/stats/me").catch(() => null);
        if (s) setStats({ streak: s.data.current_streak, score: s.data.total_score });
      }
    })();
  }, [user]);

  const dailyScore = (theme.score || 0) + (promo.score || 0);

  return (
    <div>
      {/* Hero */}
      <section className="max-w-5xl mx-auto px-4 pt-10 sm:pt-16 pb-6 text-center">
        <div className="text-[10px] sm:text-xs font-mono uppercase tracking-[0.35em] text-red-500 mb-3">
          The daily wrestling audio guessing game
        </div>
        <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-black uppercase tracking-tight leading-none">
          <span className="text-white">Recognize the</span><br />
          <span className="text-red-500 drop-shadow-[0_0_25px_rgba(220,38,38,0.5)]">entrance</span>
          <span className="text-white"> before the </span>
          <span className="text-white underline decoration-red-600 decoration-4 underline-offset-8">crowd does</span>
        </h1>
        <p className="mt-6 text-slate-400 text-sm sm:text-base max-w-xl mx-auto">
          Six second-long windows. Two daily puzzles. One shot at glory.
        </p>
      </section>

      {/* Streak strip */}
      <section className="max-w-5xl mx-auto px-4 mb-6">
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 backdrop-blur p-4 sm:p-5 grid grid-cols-3 gap-3 text-center">
          <MetricBadge icon={Flame} label="Streak" value={stats.streak} color="text-orange-400" testid="hero-streak" />
          <MetricBadge icon={Trophy} label="Today's Score" value={dailyScore} color="text-red-500" testid="hero-daily-score" />
          <MetricBadge icon={Clock} label="Next Puzzle" value={countdown} color="text-slate-200" mono testid="hero-countdown" />
        </div>
      </section>

      {/* Game Cards */}
      <section className="max-w-5xl mx-auto px-4 grid md:grid-cols-2 gap-4 sm:gap-6 mb-10">
        <GameCard
          testid="card-theme"
          icon={Play}
          label="Daily Entrance Theme"
          title="Guess the walkout"
          desc="From ring bell to riff — how fast can you name them?"
          available={!!themeInfo?.puzzle}
          played={theme.played}
          won={theme.won}
          onClick={() => nav("/daily/theme")}
        />
        <GameCard
          testid="card-promo"
          icon={Mic2}
          label="Daily Promo"
          title="Identify the voice"
          desc="A mic hits their hand. You've got seconds to say who."
          comingSoon
          available={false}
          played={false}
          won={false}
          onClick={() => nav("/daily/promo")}
        />
      </section>

      {/* How it works */}
      <section className="max-w-4xl mx-auto px-4 pb-16">
        <div className="ring-rope pt-8">
          <div className="text-xs font-mono uppercase tracking-widest text-red-500 mb-2">Rulebook</div>
          <h2 className="font-display text-2xl sm:text-3xl font-black uppercase text-white">How it works</h2>
          <p className="text-slate-400 mt-2 max-w-2xl">
            Hit play — you get a 0.1-second clip. If you miss, we unlock more: 1s, 3s, 5s, 7s, 10s.
            The earlier you nail it, the more points. Alt names, nicknames, faction names — all count.
          </p>
          <Link to="/how-to-play"
            className="inline-flex items-center gap-1 mt-3 text-red-500 hover:text-red-400 font-semibold text-sm uppercase tracking-wider"
            data-testid="link-how">
            Full rulebook <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}

function MetricBadge({ icon: I, label, value, color, mono, testid }) {
  return (
    <div>
      <div className="flex items-center justify-center gap-1 text-[10px] uppercase tracking-widest text-slate-500 font-mono">
        <I className={`h-3 w-3 ${color}`} /> {label}
      </div>
      <div className={`mt-1 font-black text-xl sm:text-2xl ${mono ? "font-mono" : "font-display"} ${color}`}
           data-testid={testid}>
        {value || 0}
      </div>
    </div>
  );
}

function GameCard({ icon: I, label, title, desc, available, played, won, onClick, testid, comingSoon }) {
  return (
    <Card onClick={onClick}
      data-testid={testid}
      className="group cursor-pointer relative overflow-hidden bg-slate-900/70 hover:bg-slate-900 border-slate-800 hover:border-red-600/60 p-5 sm:p-6 transition-all">
      <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full bg-red-600/10 blur-3xl group-hover:bg-red-600/20 transition-all" />
      <div className="flex items-start justify-between relative">
        <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-red-500">
          <I className="h-4 w-4" /> {label}
        </div>
        {comingSoon ? (
          <span className="text-[10px] px-2 py-1 rounded font-mono uppercase bg-amber-950/50 text-amber-400 border border-amber-800"
                data-testid={`${testid}-status`}>Coming soon</span>
        ) : available ? (
          played ? (
            <span className={`text-[10px] px-2 py-1 rounded font-mono uppercase ${won ? "bg-emerald-950/50 text-emerald-400 border border-emerald-800" : "bg-red-950/50 text-red-400 border border-red-900"}`}
                  data-testid={`${testid}-status`}>
              {won ? "Solved" : "Missed"}
            </span>
          ) : (
            <span className="text-[10px] px-2 py-1 rounded font-mono uppercase bg-slate-800 text-slate-300 border border-slate-700"
                  data-testid={`${testid}-status`}>Not played</span>
          )
        ) : (
          <span className="text-[10px] px-2 py-1 rounded font-mono uppercase bg-slate-800 text-slate-500 border border-slate-700">
            No puzzle today
          </span>
        )}
      </div>
      <h3 className="font-display font-black text-2xl sm:text-3xl mt-3 uppercase leading-tight">{title}</h3>
      <p className="text-slate-400 text-sm mt-1">{desc}</p>
      <Button className="mt-4 bg-red-600 hover:bg-red-500 text-white font-bold uppercase tracking-wide w-full sm:w-auto">
        {comingSoon ? "Peek" : available ? (played ? "Replay" : "Play now") : "See archive"} <ArrowRight className="h-4 w-4 ml-1" />
      </Button>
    </Card>
  );
}
